import * as React from 'react';

import { Button } from '@xing-com/button';
import { Headline, Hero, BodyCopy } from '@xing-com/typography';

import * as Styled from './empty-state.styles';
import type { EmptyStateProps } from './empty-state.types';

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      bodyCopyProps,
      bodyCopyText,
      buttonProps,
      buttonText,
      children,
      className,
      headlineProps,
      headlineText,
      illustration: Illustration,
      illustrationProps,
      size = 'medium',
      variant = 'default',
      withBackground,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const headlineId = React.useId();
    const isMediumSize = size === 'medium';
    const isEngageVariant = variant === 'engage';
    const hasTextContent = headlineText || bodyCopyText;

    const renderHeadline = (): JSX.Element => {
      if (isMediumSize) {
        return (
          <Headline id={headlineId} noMargin size="xlarge" {...headlineProps}>
            {headlineText}
          </Headline>
        );
      } else {
        return (
          <Hero id={headlineId} noMargin size="small" {...headlineProps}>
            {headlineText}
          </Hero>
        );
      }
    };

    const renderBodyCopy = (): JSX.Element => {
      if (isMediumSize) {
        return (
          <BodyCopy
            lineHeight="increased"
            noMargin
            size="medium"
            {...bodyCopyProps}
          >
            {bodyCopyText}
          </BodyCopy>
        );
      } else {
        return (
          <BodyCopy
            lineHeight="increased"
            noMargin
            size="xlarge"
            {...bodyCopyProps}
          >
            {bodyCopyText}
          </BodyCopy>
        );
      }
    };

    const renderButton = (): JSX.Element => {
      if (isEngageVariant) {
        return (
          <Button {...buttonProps} size="medium" variant="secondary">
            {buttonText}
          </Button>
        );
      } else {
        return (
          <Button {...buttonProps} size="medium" variant="tertiary">
            {buttonText}
          </Button>
        );
      }
    };

    return (
      <Styled.EmptyState
        $size={size}
        $withBackground={withBackground}
        aria-labelledby={headlineText ? headlineId : undefined}
        className={className}
        data-xds="EmptyState"
        ref={forwardedRef}
        role="region"
        {...props}
      >
        {Illustration && (
          <Styled.IllustrationContainer>
            <Illustration
              aria-hidden="true"
              height={92}
              width={92}
              {...illustrationProps}
            />
          </Styled.IllustrationContainer>
        )}
        {hasTextContent && (
          <Styled.TextContent $size={size} $variant={variant}>
            {headlineText && renderHeadline()}
            {bodyCopyText && renderBodyCopy()}
          </Styled.TextContent>
        )}
        {children && <Styled.CustomContent>{children}</Styled.CustomContent>}
        {buttonText && (
          <Styled.ButtonContainer>
            {buttonText && renderButton()}
          </Styled.ButtonContainer>
        )}
      </Styled.EmptyState>
    );
  }
);

EmptyState.displayName = 'EmptyState';
